import { useState, useRef } from 'react';
import { BookOpen, X, Upload, Loader2, Check, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { KIND_MAGAZINE } from '@/lib/pressstrKinds';

interface CreateMagazineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function CreateMagazineDialog({ open, onOpenChange, onSuccess }: CreateMagazineDialogProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { mutate: createEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploadingImage } = useUploadFile();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [website, setWebsite] = useState('');
  const [language, setLanguage] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManual) {
      setSlug(slugify(value));
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image file.', variant: 'destructive' });
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setImageFile(cloned);
    setImagePreview(URL.createObjectURL(cloned));
    e.target.value = '';
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !slug.trim()) {
      toast({ title: 'Missing fields', description: 'Title and slug are required.', variant: 'destructive' });
      return;
    }
    if (!user) return;

    setIsSubmitting(true);
    try {
      let finalImageUrl = imageUrl.trim();

      // Upload image if selected
      if (imageFile) {
        const tags = await uploadFile(imageFile);
        const urlTag = tags.find(([n]) => n === 'url');
        if (urlTag?.[1]) finalImageUrl = urlTag[1];
      }

      const eventTags: string[][] = [
        ['d', slug.trim()],
        ['title', title.trim()],
        ['alt', `Magazine: ${title.trim()}`],
      ];

      if (finalImageUrl) eventTags.push(['image', finalImageUrl]);
      if (summary.trim()) eventTags.push(['summary', summary.trim()]);
      if (website.trim()) eventTags.push(['url', website.trim()]);
      if (language.trim()) eventTags.push(['l', language.trim().toLowerCase()]);
      for (const t of tags) eventTags.push(['t', t]);

      createEvent(
        {
          kind: KIND_MAGAZINE,
          content: description.trim(),
          tags: eventTags,
        },
        {
          onSuccess: () => {
            toast({ title: 'Magazine created!', description: `"${title}" has been published.` });
            onSuccess?.();
            handleClose();
          },
          onError: (err) => {
            toast({ title: 'Failed to publish', description: err.message, variant: 'destructive' });
            setIsSubmitting(false);
          },
        }
      );
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setSlug('');
    setSlugManual(false);
    setSummary('');
    setDescription('');
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
    setWebsite('');
    setLanguage('');
    setTagInput('');
    setTags([]);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onOpenChange(false);
    }
  };

  const busy = isSubmitting || isPublishing || isUploadingImage;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Create Magazine
          </DialogTitle>
          <DialogDescription>
            Create a new magazine publication on Nostr.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Cover Image */}
          <div>
            <Label className="text-sm font-medium">Cover Image</Label>
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
            <div className="mt-2 flex items-start gap-4">
              {imagePreview ? (
                <div className="relative w-24 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    onClick={() => { setImageFile(null); setImagePreview(null); }}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="w-24 aspect-[2/3] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-muted/50 transition-colors shrink-0"
                >
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                </button>
              )}
              <div className="flex-1 space-y-2">
                <p className="text-xs text-muted-foreground">Upload a cover image (portrait 2:3 ratio recommended). Or paste a URL below.</p>
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/cover.jpg"
                  className="text-xs"
                  disabled={!!imageFile}
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="mag-title">Title *</Label>
            <Input
              id="mag-title"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="My Awesome Magazine"
              className="mt-1.5"
            />
          </div>

          {/* Slug */}
          <div>
            <Label htmlFor="mag-slug">Slug (unique identifier) *</Label>
            <Input
              id="mag-slug"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
              placeholder="my-awesome-magazine"
              className="mt-1.5 font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Used to uniquely identify this magazine. Cannot be changed later.
            </p>
          </div>

          {/* Summary */}
          <div>
            <Label htmlFor="mag-summary">Short Summary</Label>
            <Input
              id="mag-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="A brief one-liner about this magazine"
              className="mt-1.5"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="mag-desc">Full Description (Markdown)</Label>
            <Textarea
              id="mag-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="About this magazine..."
              rows={3}
              className="mt-1.5"
            />
          </div>

          {/* Website */}
          <div>
            <Label htmlFor="mag-website">Website URL</Label>
            <Input
              id="mag-website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://mymagazine.com"
              className="mt-1.5"
            />
          </div>

          {/* Language */}
          <div>
            <Label htmlFor="mag-lang">Language Code</Label>
            <Input
              id="mag-lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              placeholder="en, de, fr, es..."
              className="mt-1.5 w-32"
              maxLength={5}
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Topics / Tags</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="bitcoin, tech, art..."
              />
              <Button type="button" variant="secondary" onClick={addTag}>Add</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(t => (
                  <Badge key={t} variant="secondary" className="pl-2">
                    #{t}
                    <button onClick={() => setTags(tags.filter(x => x !== t))} className="ml-1 hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} className="flex-1" disabled={busy || !title.trim() || !slug.trim()}>
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publishing...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" />Create Magazine</>
              )}
            </Button>
            <Button variant="outline" onClick={handleClose} disabled={busy}>Cancel</Button>
          </div>

          {isUploadingImage && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading cover image to Blossom...
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
